import { useEffectWithWeb3 } from '@trustpad/launchpad';
import { CONTRACTS } from '@trustpad/launchpad/dist/abi';
import {
  MulticallCall,
  multicallv2,
} from '@trustpad/launchpad/dist/shared/utils/multicall';
import { StakingPool } from '@trustpad/launchpad/dist/staking/types';

import { useEffect } from 'react';
import { useImmer } from 'use-immer';
import { toBN } from 'web3-utils';

import { StakingStats } from '~features/tiers/components/StakingStats/StakingStatsWrapper';
import { normalizeDecimals, toEthN } from '~utils';

/**
 * Fetch all pools stats in one multicall.
 */
export function useStakingStatsBatch(
  pools: StakingPool[],
  tokenPrice: number,
): Record<string, StakingStats> {
  const [stats, setStats] = useImmer<Record<string, StakingStats>>({});

  useEffect(() => {
    setStats((draft) => {
      for (let k in draft) {
        draft[k].tvl = draft[k].lockedTokens * tokenPrice;
      }
    });
  }, [tokenPrice, stats, setStats]);

  useEffectWithWeb3((web3) => {
    const calls = pools.flatMap(
      (p) =>
        [
          {
            address: p.rewardToken.address,
            name: 'balanceOf',
            params: [p.address],
            contractName: 'token',
          },
          {
            address: p.address,
            name: 'stakersCount',
            contractName: 'staking',
          },
        ] as MulticallCall[],
    );

    multicallv2({
      web3,
      calls,
      abi: { staking: CONTRACTS.staking.v2.abi, token: CONTRACTS.erc20.abi },
      options: { unwrapValues: true },
    }).then((results) => {
      const stats = pools.reduce((acc, p, idx) => {
        const lockedTokens = toEthN(
          normalizeDecimals(toBN(results[idx * 2]), p.rewardToken.decimals),
        );
        acc[p.address] = {
          lockedTokens,
          stakers: results[idx * 2 + 1],
          tvl: lockedTokens * tokenPrice,
        };
        return acc;
      }, {} as Record<string, StakingStats>);
      setStats(stats);
    });
  });

  return stats;
}
